import { Button } from '@base-ui/react/button';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LANGUAGE_OPTIONS, type Language } from '@/admin/languages';
import { BibleTabs } from '@/admin/publishing/BibleTabs';
import { InlineName } from '@/admin/publishing/InlineName';
import { OrderDialog } from '@/admin/publishing/OrderDialog';
import buttonStyles from '@/admin/styles/button.module.css';
import { ErrorText, FieldHint, Select } from '@/admin/ui/Form';
import { PageTitle } from '@/admin/ui/Page';
import api from '@/api';
import type { components } from '@/api/qraft/schema';

type Testament = components['schemas']['AdminBibleTestament'];

type BooksSearch = { language?: Language };

const LANGUAGES = LANGUAGE_OPTIONS.map(([code]) => code);

export const Route = createFileRoute('/admin/bible/books')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (s: Record<string, unknown>): BooksSearch => ({
    language: LANGUAGES.find((l) => l === s.language),
  }),
  component: BooksPage,
});

function invalidateTestaments() {
  return api.adminListBibleTestaments.invalidateQueries();
}

function BooksPage() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const language = Route.useSearch().language ?? 'EN';
  const { data, isLoading, error } = api.adminListBibleTestaments.useQuery({
    query: { language },
  });

  return (
    <div>
      <PageTitle>{t('admin.nav.bible')}</PageTitle>
      <BibleTabs active="books" />
      <Bar>
        <Select
          aria-label={t('admin.common.language')}
          value={language}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            navigate({
              search: { language: e.target.value as Language },
              replace: true,
            })
          }
        >
          {LANGUAGE_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {t(label)}
            </option>
          ))}
        </Select>
      </Bar>
      {isLoading ? (
        <FieldHint>{t('admin.common.loading')}</FieldHint>
      ) : error || !data ? (
        <ErrorText>{t('admin.publishing.books.errorLoading')}</ErrorText>
      ) : (
        [...data]
          .sort((a, b) => a.order - b.order)
          .map((testament) => (
            <TestamentSection key={testament.id} testament={testament} />
          ))
      )}
    </div>
  );
}

function TestamentSection({ testament }: { testament: Testament }) {
  const { t } = useTranslation();
  const [ordering, setOrdering] = useState(false);
  const renameTestament = api.adminUpdateBibleTestament.useMutation(undefined, {
    onSuccess: invalidateTestaments,
  });
  const renameBook = api.adminUpdateBibleBook.useMutation(undefined, {
    onSuccess: invalidateTestaments,
  });
  const moveBook = api.adminMoveBibleBook.useMutation(undefined, {
    meta: { toast: false },
  });
  const books = [...testament.books].sort((a, b) => a.order - b.order);

  return (
    <Section aria-label={testament.name}>
      <Head>
        <InlineName
          strong
          value={testament.name}
          label={testament.name}
          onSave={(name) =>
            renameTestament.mutateAsync({
              path: { testament_id: testament.id },
              body: { name },
            })
          }
        />
        <Button
          className={buttonStyles.Button}
          disabled={books.length < 2}
          onClick={() => setOrdering(true)}
        >
          {t('admin.publishing.books.reorder')}
        </Button>
      </Head>
      <List>
        {books.map((book, index) => (
          <Item key={book.id}>
            <Place>{index + 1}</Place>
            <InlineName
              value={book.name}
              label={book.name}
              onSave={(name) =>
                renameBook.mutateAsync({
                  path: { book_id: book.id },
                  body: { name },
                })
              }
            />
            <Count>
              {t('admin.publishing.books.chapters', {
                count: book.chapters_count,
                interpolation: { escapeValue: false },
              })}
            </Count>
          </Item>
        ))}
      </List>
      <OrderDialog
        open={ordering}
        onClose={() => setOrdering(false)}
        title={t('admin.publishing.books.reorderTitle', {
          name: testament.name,
          interpolation: { escapeValue: false },
        })}
        items={books.map((b) => ({ id: b.id, label: b.name }))}
        onMove={async (id, position) => {
          await moveBook.mutateAsync({
            path: { book_id: id },
            body: { position },
          });
          await invalidateTestaments();
        }}
      />
    </Section>
  );
}

const Bar = styled.div`
  margin: var(--component-margin) 0;

  select {
    width: auto;
  }
`;

const Section = styled.section`
  background: var(--component-color);
  border-radius: var(--area-border-radius);
  box-shadow: var(--shadow-md);
  margin-bottom: var(--component-margin);
  padding: var(--inner-padding);
`;

const Head = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Item = styled.li`
  align-items: center;
  border-top: 1px solid var(--grey-200);
  display: flex;
  gap: 0.75rem;
  padding: 0.375rem 0;
`;

const Place = styled.span`
  color: var(--grey-600);
  font-size: 0.75rem;
  min-width: 1.75rem;
`;

const Count = styled.span`
  color: var(--grey-600);
  font-size: 0.75rem;
  margin-left: auto;
`;
